import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./Header.css";
import {
  isAuthenticated,
  isPlanPurchased,
  getPlanDaysLeft,
  isChatting,
} from "../../../utils/api-utils";
import { useNavigate } from "react-router-dom";
import Chatting from "../../../Components/Chatting/Chatting.js";
import VivApi from "../../../api";
import chatWebSocket from "../../../services/chatWebSocket.service";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [shrunk,  setShrunk]  = useState(false);
  const [activeChatUser, setActiveChatUser] = useState(() => isChatting());
  const [, forceUpdate] = useState(0);
  const [pendingSummary, setPendingSummary] = useState(null);
  const dropdownRef = useRef(null);

  // Sync plan status from backend on every mount — handles cross-device purchases
  useEffect(() => {
    if (!isAuthenticated()) return;

    const fetchPending = () => {
      VivApi.Chat.getPendingSummary().then(r => {
        const { pendingMessages, pendingSenders } = r.data || {};
        setPendingSummary(pendingMessages > 0 ? { pendingMessages, pendingSenders } : null);
      }).catch(() => {});
    };

    VivApi.Auth.userMe().then(res => {
      const user = res.data?.user || res.data;
      const isPremium = user?.isPremium === 'yes';
      const expiry = user?.premiumPlanExpiry;
      const planName = user?.premiumPlanName || 'Premium';

      if (isPremium && expiry && expiry > new Date().toISOString().slice(0, 10)) {
        const prev = localStorage.getItem('planName');
        localStorage.setItem('planName', planName);
        localStorage.setItem('planExpiry', expiry);
        localStorage.setItem('planId', user?.premiumPlanId || '');
        if (!prev) forceUpdate(n => n + 1);
      } else {
        if (localStorage.getItem('planName')) {
          localStorage.removeItem('planName');
          localStorage.removeItem('planExpiry');
          localStorage.removeItem('planId');
          forceUpdate(n => n + 1);
        }
      }
      // Fetch unread count for all users on mount
      fetchPending();
    }).catch(() => {});

    // Keep WS alive for all authenticated users so Header can react to new messages
    chatWebSocket.connect();
    const unsubMsg = chatWebSocket.onMessage((data) => {
      if (data.type === 'message') fetchPending();
    });

    return () => unsubMsg();
  }, []);

  // React to chatUser changes set from other components (no page reload needed)
  useEffect(() => {
    const handler = () => setActiveChatUser(isChatting());
    window.addEventListener("chatUserChanged", handler);
    return () => window.removeEventListener("chatUserChanged", handler);
  }, []);

  // Clear the pending badge when the user reads messages in the chat page
  useEffect(() => {
    const handler = () => {
      VivApi.Chat.getPendingSummary().then(r => {
        const { pendingMessages, pendingSenders } = r.data || {};
        setPendingSummary(pendingMessages > 0 ? { pendingMessages, pendingSenders } : null);
      }).catch(() => {});
    };
    window.addEventListener("chatMessagesRead", handler);
    return () => window.removeEventListener("chatMessagesRead", handler);
  }, []);
  
  const handleLogOut = () => {
    chatWebSocket.disconnect();
    localStorage.clear();
    navigate("/login");
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // On every route change: scroll to top and reset scroll state
  useEffect(() => {
    window.scrollTo(0, 0);
    setScrolled(false);
    setShrunk(false);
    setMenuOpen(false);
    document.body.classList.remove('gla_first_step', 'gla_second_step');
  }, [location.pathname]);

  // React-owned scroll handler — drives both body classes (for theme CSS)
  // and local state (for direct inline-style control on the nav)
  useEffect(() => {
    const onScroll = () => {
      const top = window.scrollY;
      if (top > 200) {
        setScrolled(true); setShrunk(true);
        document.body.classList.add('gla_first_step', 'gla_second_step');
      } else if (top > 50) {
        setScrolled(true); setShrunk(false);
        document.body.classList.add('gla_first_step');
        document.body.classList.remove('gla_second_step');
      } else {
        setScrolled(false); setShrunk(false);
        document.body.classList.remove('gla_first_step', 'gla_second_step');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);


  return (
    <header style={{ height: shrunk ? '52px' : '83px' }}>
      <nav
        className="gla_transp_nav"
        style={{
          height:          shrunk  ? '52px'                        : '83px',
          backgroundColor: scrolled ? 'rgba(255,255,255,0.97)'     : 'rgba(10,5,15,0.92)',
          boxShadow:       scrolled ? '0 2px 8px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.3)',
          transition: 'height 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <div className="container">
          {/* ── Animated hamburger (mobile only, left side) ── */}
          <button
            className={`gla_main_menu mobile-hamburger${menuOpen ? ' active' : ''}${scrolled ? ' scrolled' : ''}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
          </button>

          <div className="gla_logo_container clearfix">
            <Link to="/">
              <img
                src="/assets/images/logo.png"
                alt=""
                className="gla_logo_rev"
              />
            </Link>
          </div>

          {/* ── Mobile drawer backdrop ── */}
          <div
            className={`mobile-drawer-backdrop${menuOpen ? ' active' : ''}`}
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />

          {/* ── Mobile drawer panel ── */}
          <nav
            className={`mobile-drawer${menuOpen ? ' active' : ''}`}
            aria-label="Mobile navigation"
          >
            <div className="mobile-drawer-header">
              <img src="/assets/images/logo.png" alt="Vivah Bandh" className="mobile-drawer-logo" />
              <button
                className="mobile-drawer-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <span></span>
                <span></span>
              </button>
            </div>

            <ul className="mobile-drawer-links">
              {/* Home — mobile drawer only */}
              <li>
                <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-active' : ''} onClick={() => setMenuOpen(false)}>
                  <i className="ti ti-home"></i> Home
                </NavLink>
              </li>
              {!isAuthenticated() && (
                <>
                  <li><Link to="/about" onClick={() => setMenuOpen(false)}><i className="ti ti-info-alt"></i> About Us</Link></li>
                  <li><Link to="/contact-us" onClick={() => setMenuOpen(false)}><i className="ti ti-email"></i> Contact Us</Link></li>
                  <li><Link to="/login" onClick={() => setMenuOpen(false)} className="mobile-drawer-cta"><i className="ti ti-key"></i> Login</Link></li>
                </>
              )}
              {isAuthenticated() && (
                <>
                  <li>
                    <NavLink to="/discover" className={({ isActive }) => isActive ? 'nav-active' : ''} onClick={() => setMenuOpen(false)}>
                      <i className="ti ti-search"></i> Discover
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/favourite" className={({ isActive }) => isActive ? 'nav-active' : ''} onClick={() => setMenuOpen(false)}>
                      <i className="ti ti-heart"></i> Favourites
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/request" className={({ isActive }) => isActive ? 'nav-active' : ''} onClick={() => setMenuOpen(false)}>
                      <i className="ti ti-bell"></i> Requests
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/search" className={({ isActive }) => isActive ? 'nav-active' : ''} onClick={() => setMenuOpen(false)}>
                      <i className="ti ti-filter"></i> Search
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/chatting" className={({ isActive }) => isActive ? 'nav-active' : ''} onClick={() => setMenuOpen(false)}>
                      <i className={isPlanPurchased() ? "ti ti-comment-alt" : "ti ti-lock"}></i>
                      Messages
                      {pendingSummary && (
                        <span className="nav-msg-badge">{pendingSummary.pendingMessages}</span>
                      )}
                    </NavLink>
                  </li>
                  {!isPlanPurchased() && (
                    <li className="highlight-upgrade-mobile">
                      <Link to="/update" onClick={() => setMenuOpen(false)}><i className="ti ti-crown"></i> Upgrade Now</Link>
                    </li>
                  )}
                  <li className="mobile-drawer-divider"></li>
                  <li><Link to="/my-profile" onClick={() => setMenuOpen(false)}><i className="ti ti-id-badge"></i> My Profile</Link></li>
                  <li><Link to="/account-settings" onClick={() => setMenuOpen(false)}><i className="ti ti-settings"></i> Account Settings</Link></li>
                  <li>
                    <Link to="/" onClick={(e) => { setMenuOpen(false); handleLogOut(e); }} className="mobile-drawer-logout">
                      <i className="ti ti-power-off"></i> Logout
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>

          {isAuthenticated() && (
            <div className="gla_search_block search_match">
              <div className="gla_search_block_link gla_user_profile" ref={dropdownRef}>
                <button 
                  className="profile-menu-button" 
                  onClick={toggleProfileDropdown}
                  aria-label="Profile Menu"
                  aria-expanded={profileDropdownOpen}
                >
                  <i className="ti ti-user"></i>
                  <i className={`ti ti-angle-down dropdown-arrow ${profileDropdownOpen ? 'open' : ''}`}></i>
                </button>
                <ul className={`profile-dropdown ${profileDropdownOpen ? 'show' : ''}`}>
                  {isPlanPurchased() && (
                    <li className="dropdown-plan-badge" style={{padding:'8px 16px',borderBottom:'1px solid #f5e6ea',pointerEvents:'none'}}>
                      <span style={{display:'flex',alignItems:'center',gap:'6px',color:'#e91e63',fontWeight:600,fontSize:'0.82rem'}}>
                        <i className="ti ti-crown"></i> {isPlanPurchased()} Member
                      </span>
                      {getPlanDaysLeft() !== null && (
                        <span style={{display:'block',fontSize:'0.75rem',color:'#888',marginTop:'2px'}}>
                          {getPlanDaysLeft()} day{getPlanDaysLeft() !== 1 ? 's' : ''} remaining
                        </span>
                      )}
                    </li>
                  )}
                  <li>
                    <Link to="/my-profile" onClick={() => setProfileDropdownOpen(false)}>
                      <i className="ti ti-id-badge"></i>
                      <span> My Profile</span>
                    </Link>
                  </li>
                  
                  <li>
                    <Link to="/account-settings" onClick={() => setProfileDropdownOpen(false)}>
                      <i className="ti ti-settings"></i>
                      <span> Account Settings</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/chatting" onClick={() => setProfileDropdownOpen(false)}>
                      <i className="ti ti-comment"></i>
                      <span> Messages</span>
                    </Link>
                  </li>
                  <li className="dropdown-divider"></li>
                  <li>
                    <Link to="/" onClick={handleLogOut} className="logout-link">
                      <i className="ti ti-power-off"></i>
                      <span> Logout</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div className="gla_default_menu">
            <ul>
              {!isAuthenticated() && (
                <>
                  <li>
                    <Link to="/about">About Us</Link>
                  </li>
                  <li>
                    <Link to="/contact-us">Contact Us</Link>
                  </li>
                </>
              )}

              {isAuthenticated() && (
                <>
                  <li>
                    <NavLink to="/discover" className={({ isActive }) => isActive ? "nav-active" : ""}>
                      Discover
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/favourite" className={({ isActive }) => isActive ? "nav-active" : ""}>
                      Favourites
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/request" className={({ isActive }) => isActive ? "nav-active" : ""}>
                      Requests
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/search" className={({ isActive }) => isActive ? "nav-active" : ""}>
                      Search
                    </NavLink>
                  </li>
                </>
              )}
              {isAuthenticated() && (
                <li style={{ position: 'relative' }}>
                  <NavLink to="/chatting" className={({ isActive }) => isActive ? "nav-active" : ""}>
                    {!isPlanPurchased() && <i className="ti ti-lock" style={{ fontSize: '0.8rem', marginRight: 4 }}></i>}
                    Messages
                    {pendingSummary && (
                      <span className="nav-msg-badge">{pendingSummary.pendingMessages}</span>
                    )}
                  </NavLink>
                </li>
              )}
              {isAuthenticated() && !isPlanPurchased() && (
                <li className="highlight-upgrade">
                  <Link to="/update">Upgrade Now</Link>
                </li>
              )}
              {isAuthenticated() && isPlanPurchased() && (
                <li className="plan-badge-nav">
                  <span style={{display:'inline-flex',alignItems:'center',gap:'8px',padding:'8px 20px',background:'linear-gradient(135deg,#f59e0b,#f97316)',color:'#fff',borderRadius:'30px',fontSize:'1.15rem',fontWeight:700,letterSpacing:'0.4px',whiteSpace:'nowrap',boxShadow:'0 2px 10px rgba(245,158,11,0.45)',cursor:'default'}}>
                    <i className="ti ti-crown" style={{fontSize:'1.1rem'}}></i>
                    {isPlanPurchased()}
                    {getPlanDaysLeft() !== null && (
                      <span style={{fontWeight:500,opacity:0.92}}>· {getPlanDaysLeft()}d left</span>
                    )}
                  </span>
                </li>
              )}
              {!isAuthenticated() && (
                <li>
                  <Link to="/login">Login</Link>
                </li>
              )}
            </ul>
          </div>
          {activeChatUser && (
            <Chatting
              chatUser={activeChatUser}
              onClose={() => {
                localStorage.removeItem("chatUser");
                window.dispatchEvent(new Event("chatUserChanged"));
              }}
            />
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
